import { arrayMove } from "@dnd-kit/sortable";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import {
  createErrorResult,
  errors,
  hasRoutingWaypointAtRouteEnds,
  IStationRouteStationInfo,
  Log,
  reorderStationRouteStationsByOrderedArray,
  SimpleResult,
  stationRouteDraggingStationsTempIdsAtom,
  TDragDropStationRouteSortable,
  useTimezone,
} from "@ryde/shared";

import { useStationRouteDtoActions } from "./use-station-route-dto-actions";

export const useDragStationRouteStationsReorder = () => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const { updateEditingStationRouteComputedData } = useStationRouteDtoActions();
  const setTempDragStationsIds = useSetRecoilState(
    stationRouteDraggingStationsTempIdsAtom,
  );

  const dragReorderDistancesStationRoute = useEvent(
    async (
      source: TDragDropStationRouteSortable,
      destination: TDragDropStationRouteSortable,
    ): Promise<SimpleResult<void>> => {
      Log.debug(
        "Drag reorder stations payloads",
        source?.payload,
        destination?.payload,
      );
      const stationRouteId = source?.payload?.stationRouteDto?.id || 0;
      const stationRouteToId = destination?.payload?.stationRouteDto?.id || 0;

      if (stationRouteId !== stationRouteToId) {
        return new SimpleResult({
          success: false,
          error: t("errors.cantReorderStationOfDifferentStationRoutes", {
            stationRouteId1: stationRouteId || "-",
            stationRouteId2: stationRouteToId || "-",
          }),
        });
      }

      const stationRouteDto = source?.payload?.stationRouteDto;

      if (!stationRouteDto) {
        return createErrorResult(
          "errors.cantReorderStationInvalidDragStationRouteObject",
        );
      }

      const stationsOldIds = [...source.sortable.items];
      const oldIndex = source.sortable.index;
      const newIndex = destination.sortable.index;

      if (oldIndex === newIndex) {
        Log.warn(t(errors.ride.sameIndexReturnedNoNeedToReorder));
        return new SimpleResult({
          success: false,
          error: t(errors.ride.sameIndexReturnedNoNeedToReorder),
        });
      }

      const stationsUpdatedIds = arrayMove(stationsOldIds, oldIndex, newIndex);

      const reorderedStations = stationsUpdatedIds
        .map(stationId =>
          stationRouteDto.computedData?.stationInfos?.find(
            station => station.id === stationId,
          ),
        )
        .filter((station): station is IStationRouteStationInfo =>
          Boolean(station),
        );

      if (hasRoutingWaypointAtRouteEnds(reorderedStations)) {
        return createErrorResult(
          errors.ride.routingWaypointCannotBeRouteEndpoint,
        );
      }

      setTempDragStationsIds(stationsUpdatedIds);
      Log.debug(
        `Stations order before and after sorting for stationRoute id ${stationRouteId}:`,
        stationsOldIds,
        stationsUpdatedIds,
      );

      if (!source?.payload?.stationRouteDto) {
        return createErrorResult(errors.common.invalidDragData);
      }

      if ((stationRouteDto.id || 0) !== stationRouteId) {
        return createErrorResult(errors.common.invalidDragData);
      }

      const computedDataRecounted =
        reorderStationRouteStationsByOrderedArray(
          stationRouteDto.computedData,
          stationsUpdatedIds,
          timezone,
        ) || {};
      Log.debug(
        "Composed map data after station drag for checking dry save stationRoute:",
        computedDataRecounted,
      );

      const updateResult = await updateEditingStationRouteComputedData({
        computedData: computedDataRecounted || {},
        direction: stationRouteDto?.metadata?.direction,
        optimize: false,
      });

      setTempDragStationsIds(undefined);

      return updateResult;
    },
  );
  return { dragReorderDistancesStationRoute };
};
